import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { toast } from "sonner";
import {
  Banknote, Landmark, Wallet, PiggyBank, Leaf, ShieldCheck, Building2, Gauge, Activity,
} from "lucide-react";
import { CLASS_TONE } from "./FundabilityPanel";

const CARD_DEFS = [
  { key: "total_investment_required", label: "Total Sustainability Investment Required", icon: Banknote, fmt: formatSgd },
  { key: "grant_contribution", label: "Indicative Grant Contribution", icon: Landmark, fmt: formatSgd },
  { key: "green_finance_requirement", label: "Potential Green Finance Requirement", icon: Building2, fmt: formatSgd },
  { key: "internal_capital_required", label: "Internal Capital Required", icon: Wallet, fmt: formatSgd },
  { key: "grant_supported_investment", label: "Potential Grant-Supported Investment", icon: Landmark, fmt: formatSgd },
  { key: "annual_operating_savings", label: "Annual Operating Savings", icon: PiggyBank, fmt: formatSgd },
  { key: "annual_emissions_reduction_t", label: "Est. Annual Emissions Reduction", icon: Leaf, fmt: (v) => `${formatNum(v, { decimals: 1 })} tCO₂e` },
  { key: "climate_risk_addressed", label: "Climate Risk Exposure Addressed", icon: ShieldCheck, fmt: formatSgd },
];
const STRUCT_COLORS = { grant: "#00E65B", debt: "#00B4E6", equity: "#D4FF00" };
const STRUCT_LABELS = { grant: "Grants", debt: "Debt", equity: "Internal / equity" };

export default function CfoDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/susfin/cfo").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading CFO portfolio…</div>;
  if (d.empty) return <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-cfo-empty">No data yet.</div>;

  const structTotal = Object.values(d.blended.structure || {}).reduce((s, v) => s + v, 0);
  const maxStage = Math.max(1, ...d.pipeline.map((s) => s.count));

  return (
    <div className="space-y-5" data-testid="sf-cfo-dashboard">
      <p className="text-sm text-muted-foreground -mb-1">
        How much capital do we need to execute our transition plan? · {d.project_count} active investments
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {CARD_DEFS.map(({ key, label, icon: Icon, fmt }) => (
          <div key={key} className="card-flat p-4" data-testid={`sf-cfo-card-${key}`}>
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Icon className="h-4 w-4 text-accent" /><span className="label-eyebrow text-[10px] leading-tight">{label}</span>
            </div>
            <div className="font-mono text-xl font-bold tracking-tight">{fmt(d.cards[key] || 0)}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-flat p-5" data-testid="sf-cfo-classes">
          <div className="label-eyebrow mb-3">Projects by Fundability</div>
          <div className="space-y-2">
            {d.classes.map((c) => (
              <div key={c.value} className="flex items-center justify-between border border-border rounded-sm px-3 py-2" data-testid={`sf-cfo-class-${c.value}`}>
                <span className={`text-xs uppercase tracking-wide ${(CLASS_TONE[c.value] || "").split(" ")[0]}`}>{c.label}</span>
                <span className="font-mono text-lg font-bold">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-flat p-5" data-testid="sf-cfo-blended">
          <div className="label-eyebrow mb-3 inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5 text-accent" /> Blended Economics</div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[["Portfolio NPV", formatSgd(d.blended.portfolio_npv)],
              ["Weighted IRR", d.blended.weighted_irr_pct != null ? `${d.blended.weighted_irr_pct}%` : "—"],
              ["Cost / tCO₂e", d.blended.cost_per_tco2e != null ? formatSgd(d.blended.cost_per_tco2e) : "—"],
              ["Investment cases", d.blended.cases]].map(([l, v]) => (
              <div key={l}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                <div className="font-mono text-lg font-bold">{v}</div>
              </div>
            ))}
          </div>
          <div className="label-eyebrow text-[10px] mb-2">Capital structure (modelled cases)</div>
          {structTotal > 0 ? (
            <>
              <div className="flex h-3 rounded-sm overflow-hidden border border-border" data-testid="sf-cfo-structure-bar">
                {Object.entries(d.blended.structure).map(([k, v]) => v > 0 && (
                  <div key={k} style={{ width: `${(v / structTotal) * 100}%`, background: STRUCT_COLORS[k], opacity: 0.8 }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {Object.entries(d.blended.structure).map(([k, v]) => (
                  <span key={k} className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm inline-block" style={{ background: STRUCT_COLORS[k] }} />
                    {STRUCT_LABELS[k]} <span className="font-mono">{formatSgd(v)}</span>
                  </span>
                ))}
              </div>
            </>
          ) : <p className="text-xs text-muted-foreground">No modelled investment cases yet.</p>}
        </div>

        <div className="card-flat p-5" data-testid="sf-cfo-grants">
          <div className="label-eyebrow mb-3">Grants & Bank Financing</div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border border-[#00E65B]/40 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#00E65B]">Grants secured</div>
              <div className="font-mono text-2xl font-bold">{d.grants.secured_count}</div>
              <div className="text-[10px] text-muted-foreground">{d.grants.secured_value ? formatSgd(d.grants.secured_value) : "value TBC"}</div>
            </div>
            <div className="border border-[#FFB800]/40 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#FFB800]">Grants pending</div>
              <div className="font-mono text-2xl font-bold">{d.grants.pending_count}</div>
              <div className="text-[10px] text-muted-foreground">{d.grants.pending_value ? formatSgd(d.grants.pending_value) : "value TBC"}</div>
            </div>
          </div>
          {d.bank_financing && (
            <div className="border border-[#00B4E6]/40 rounded-sm p-3 mb-4" data-testid="sf-cfo-bank">
              <div className="text-[10px] uppercase tracking-wider text-[#00B4E6] mb-1">Bank financing cases</div>
              <div className="grid grid-cols-3 gap-2">
                <div><div className="font-mono text-lg font-bold">{d.bank_financing.open_count}</div><div className="text-[9px] text-muted-foreground">open</div></div>
                <div><div className="font-mono text-sm font-bold">{formatSgd(d.bank_financing.requested_sgd)}</div><div className="text-[9px] text-muted-foreground">requested</div></div>
                <div><div className="font-mono text-sm font-bold">{formatSgd(d.bank_financing.approved_sgd)}</div><div className="text-[9px] text-muted-foreground">approved</div></div>
              </div>
            </div>
          )}
          <div className="label-eyebrow text-[10px] mb-2">Grant application pipeline</div>
          <div className="space-y-1">
            {d.application_groups.filter((g) => g.count > 0).map((g) => (
              <div key={g.key} className="flex items-center justify-between text-xs" data-testid={`sf-cfo-appgroup-${g.key}`}>
                <span className="text-muted-foreground">{g.label}</span><span className="font-mono font-bold">{g.count}</span>
              </div>
            ))}
            {d.application_groups.every((g) => g.count === 0) && <p className="text-xs text-muted-foreground">No grant applications yet.</p>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-flat p-5" data-testid="sf-cfo-pipeline">
          <div className="label-eyebrow mb-4">Investment Pipeline</div>
          <div className="grid grid-cols-7 gap-2">
            {d.pipeline.map((s) => (
              <div key={s.value} className="text-center">
                <div className="h-14 flex items-end justify-center mb-1.5">
                  <div className={`w-full max-w-[40px] rounded-t-sm ${s.count ? "bg-accent/70" : "bg-border"}`}
                    style={{ height: `${Math.max(6, (s.count / maxStage) * 100)}%` }} />
                </div>
                <div className="font-mono text-sm font-bold">{s.count}</div>
                <div className="text-[9px] text-muted-foreground leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-flat p-5" data-testid="sf-cfo-mrv">
          <div className="label-eyebrow mb-3 inline-flex items-center gap-1"><Activity className="h-3.5 w-3.5 text-accent" /> Expected vs Actual (MRV)</div>
          {d.mrv.tracking > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Achievement</div>
                <div className={`font-display text-2xl font-bold ${d.mrv.avg_achievement_pct >= 90 ? "text-[#00E65B]" : d.mrv.avg_achievement_pct >= 70 ? "text-[#FFB800]" : "text-red-400"}`}>{d.mrv.avg_achievement_pct}%</div>
                <div className="text-[10px] text-muted-foreground">{d.mrv.tracking} tracking</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Emissions / yr</div>
                <div className="font-mono text-lg font-bold">{formatNum(d.mrv.actual_emissions_t, { decimals: 1 })} t</div>
                <div className="text-[10px] text-muted-foreground">vs {formatNum(d.mrv.expected_emissions_t, { decimals: 1 })} t expected</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Savings / yr</div>
                <div className="font-mono text-lg font-bold">{formatSgd(d.mrv.actual_savings_sgd)}</div>
                <div className="text-[10px] text-muted-foreground">vs {formatSgd(d.mrv.expected_savings_sgd)} expected</div>
              </div>
            </div>
          ) : <p className="text-xs text-muted-foreground" data-testid="sf-cfo-mrv-empty">No investments with actual MRV readings yet — record actuals in the MRV tab.</p>}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
    </div>
  );
}
